import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  TablePagination,
  Tooltip,
  Fab,
  Zoom,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CheckCircleOutline,
  Add,
  InfoOutlined,
  ModeEditOutlineTwoTone,
  Print,
  HighlightOff,
  RouteSharp,
  MoreVert,
  Note,
  PlaylistAddCheck,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Inventory2Outlined,
  PersonOutlineOutlined,
  StorefrontOutlined,
  ContactPageOutlined,
  LocalShippingOutlined,
  AccountCircleOutlined,
  EventNoteOutlined,
} from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import UploadExcelShipment from "./ShipmentComponents/UploadExcelShipment";
import MultiShipmentLabel from "./ShipmentComponents/MultiShipmentLabel";
import PrintShipmentLabelModal from "./ShipmentComponents/PrintShipmentLabelModal";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import TableLoader from "components/Shared/AppTable/TableLoader";
import BaseSelect from "components/Shared/Base/BaseSelect";
import CollectionModal from "../Collection/CollectionComponents/CollectionModal";

import { useLazyListShipmentQuery } from "store/services/shipmentApi";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetDDLDestinationBranchListQuery,
} from "store/services/branchApi";
import {
  useLazyGetDdlRegionListQuery,
  useLazyGetDDLRestrictedRegionListQuery,
} from "store/services/LocationsApi/regionApi";
import { useLazyGetBranchByRegionDDLQuery } from "store/services/branchApi";

import HighlightedText from "components/Shared/HighlightedText";
import ExportShipmentExcel from "./ShipmentComponents/ExportShipmentExcel";
import { BaseTabs } from "components/Shared/BaseTabs";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useCustomParams } from "hooks/useCustomParams";
import BulkInvoicePrintModal from "./ShipmentComponents/BulkInvoicePrintModal";
import BulkRoutesModalForm from "./ShipmentComponents/BulkRoutesModalForm";
import BulkShipmentNoteModalForm from "./ShipmentComponents/BulkShipmentNoteModalForm";

const getStatusStyle = (status) => {
  const styles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Unloaded: { backgroundColor: "#FEF9E7", textColor: "#B7950B" },
    CrossDocked: { backgroundColor: "#E8DAEF", textColor: "#8E44AD" },
    SendOut: { backgroundColor: "#FADBD8", textColor: "#E74C3C" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
    DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const ShipmentListScreen = () => {
  const {
    isVendor,
    vendorId,
    isContractor,
    isCustomer,
    loggedInUser,
    isVendorStaff,
    viewEntity,
  } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { reset } = useForm({
    defaultValues: { selectedShipments: [] },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [printLabelModalOpen, setPrintLabelModalOpen] = useState(false);
  const [multiLabelOpen, setMultiLabelOpen] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [bulkRoutesOpen, setBulkRoutesOpen] = useState(false);
  const [bulkShipmentNoteOpen, setBulkShipmentNoteOpen] = useState(false);

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const { params, appendParams, resetParams } = useCustomParams();

  const from = params.from;
  const to = params.to;
  const shipmentStatus = params.shipmentStatus;
  const paymentStatus = params.paymentStatus;
  const deliveryTypes = params.deliveryTypes;
  const sortOrder = params.sortOrder || "createdAt:-1";
  const priority = params.priority;
  const sourceRegion = params.sourceRegion;
  const destinationRegion = params.destinationRegion;

  const sourceBranch = React.useMemo(
    () => params.sourceBranch?.split(",") ?? [],
    [params.sourceBranch]
  );
  const destinationBranch = React.useMemo(
    () => params.destinationBranch?.split(",") ?? [],
    [params.destinationBranch]
  );

  // Source branch — restricted for entity users
  const [triggerGetDDLBranchList, { data: branchesData }] =
    useLazyGetDDLBranchListQuery();

  // Destination branch — all branches
  const [triggerGetDDLDestinationBranchList, { data: destinationBranchesAllData }] =
    useLazyGetDDLDestinationBranchListQuery();

  // Source Province — restricted
  const [triggerGetRestrictedRegionList, { data: restrictedRegionsData }] =
    useLazyGetDDLRestrictedRegionListQuery();

  // Destination Province — all regions
  const [triggerGetDdlRegionList, { data: regionsData }] =
    useLazyGetDdlRegionListQuery();

  const [triggerGetSourceBranchesByRegion, { data: sourceBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();
  const [triggerGetDestinationBranchesByRegion, { data: destinationBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();

  const mappedBranchList = React.useMemo(() => {
    const dataArray = Array.isArray(branchesData)
      ? branchesData
      : Array.isArray(branchesData?.data)
        ? branchesData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name && item?.code)
      .map((item) => ({ label: `${item.name} - ${item.code}`, value: item._id }));
  }, [branchesData]);

  const mappedDestinationBranchList = React.useMemo(() => {
    const dataArray = Array.isArray(destinationBranchesAllData)
      ? destinationBranchesAllData
      : Array.isArray(destinationBranchesAllData?.data)
        ? destinationBranchesAllData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name && item?.code)
      .map((item) => ({ label: `${item.name} - ${item.code}`, value: item._id }));
  }, [destinationBranchesAllData]);

  // Source Province — restricted
  const mappedRestrictedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(restrictedRegionsData)
      ? restrictedRegionsData
      : Array.isArray(restrictedRegionsData?.data)
        ? restrictedRegionsData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({ label: item.name, value: item._id }));
  }, [restrictedRegionsData]);

  // Destination Province — all regions
  const mappedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(regionsData)
      ? regionsData
      : Array.isArray(regionsData?.data)
        ? regionsData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({ label: item.name, value: item._id }));
  }, [regionsData]);

  const mappedSourceRegionBranches = React.useMemo(
    () =>
      Array.isArray(sourceBranchesData?.data)
        ? sourceBranchesData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({ label: `${item.name} - ${item.code}`, value: item._id }))
        : [],
    [sourceBranchesData]
  );

  const mappedDestinationRegionBranches = React.useMemo(
    () =>
      Array.isArray(destinationBranchesData?.data)
        ? destinationBranchesData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({ label: `${item.name} - ${item.code}`, value: item._id }))
        : [],
    [destinationBranchesData]
  );

  const selectedSource = React.useMemo(
    () => mappedBranchList?.filter((branch) => sourceBranch.includes(branch.value)),
    [mappedBranchList, sourceBranch]
  );

  const selectedDestination = React.useMemo(
    () => mappedDestinationBranchList?.filter((branch) => destinationBranch.includes(branch.value)),
    [mappedDestinationBranchList, destinationBranch]
  );

  // Source Province uses restricted list
  const selectedSourceRegion = React.useMemo(
    () => mappedRestrictedRegionList?.find((region) => region.value === sourceRegion),
    [mappedRestrictedRegionList, sourceRegion]
  );

  // Destination Province uses all regions
  const selectedDestinationRegion = React.useMemo(
    () => mappedRegionList?.find((region) => region.value === destinationRegion),
    [mappedRegionList, destinationRegion]
  );

  const sourceBranchIdsFromRegion = React.useMemo(
    () => mappedSourceRegionBranches.map((branch) => branch.value),
    [mappedSourceRegionBranches]
  );

  const destinationBranchIdsFromRegion = React.useMemo(
    () => mappedDestinationRegionBranches.map((branch) => branch.value),
    [mappedDestinationRegionBranches]
  );

  const urlTab = searchParams.get("tab");
  const defaultTab = loggedInUser?.typeRefSource === "Contractor" ? "Pending" : "Active";
  const activeTab = urlTab || defaultTab;
  const keyword = searchParams.get("search") || "";
  const breadcrumbs = [{ title: "Shipment" }];

  const shipmentTabs =
    loggedInUser?.typeRefSource === "Contractor"
      ? [{ label: "Pending", value: "Pending" }]
      : [
        { label: "All", value: "Active" },
        ...(loggedInUser?.typeRefSource !== "Customer"
          ? [{ label: "Inactive", value: "Inactive" }]
          : []),
      ];

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      width: "220px",
      cell: (row) => {
        const shiId = row?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1);
        const displayId = `${shiId}${row.IsReturning ? " (R)" : ""}`;
        const path = `/app/${isVendor ? "vendor" : isCustomer ? "customer" : isVendorStaff ? "staff" : "crm"}/shipment/${row._id}/detail`;
        return (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {displayId ? <HighlightedText text={displayId} highlight={keyword} /> : "N/A"}
          </Link>
        );
      },
    },
    {
      name: "Sender",
      width: "220px",
      selector: (row) => row?.customerId?.customerUser?.name || row?.vendor?.shopName || "N/A",
      cell: (row) => (
        <Box sx={{ maxWidth: "600px", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row?.customerId?.customerUser?.name
            ? `CUS: ${row?.customerId?.customerUser?.name}`
            : row?.vendor?.shopName
              ? `VEN: ${row.vendor.shopName}`
              : "N/A"}
        </Box>
      ),
      omit: isVendor || isCustomer,
    },
    {
      name: "Receiver",
      width: "200px",
      selector: (row) => row?.receiver?.customerUser?.name || row?.receiver?.name,
      cell: (row) => {
        const name = row?.receiver?.customerUser?.name || row?.receiver?.name || "-";
        const phone = row?.receiver?.customerUser?.phone || row?.receiver?.phone || "";
        return (
          <div>
            <span className="text-sm">{name}</span>
            <br />
            {phone && <span className="text-sm text-gray-500 dark:text-gray-300">{phone}</span>}
          </div>
        );
      },
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name || "N/A",
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name || "N/A",
      cell: (row) => row?.createdBy?.name || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Chip
          label={row?.status?.toUpperCase() || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getStatusStyle(row?.status).backgroundColor,
            color: getStatusStyle(row?.status).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": { fontSize: "0.6rem", padding: "3px 5px", lineHeight: "14px" },
          }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
            ? "/app/customer/shipment"
            : isVendorStaff
              ? "/app/staff/shipment"
              : "/app/crm/shipment";
        const isEditDisabled =
          ((isVendor || isCustomer || isVendorStaff) && row?.status !== "Pending") ||
          row?.status === "Delivered" ||
          row?.isActive !== true;
        return (
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            {isContractor ? (
              <IconButton
                size="small"
                aria-label="Approve Shipment"
                color="primary"
                onClick={() => row && handleApproveClick(row)}
                disabled={!row}
              >
                <CheckCircleOutline />
              </IconButton>
            ) : (
              <>
                <Link to={`${basePath}/${row?._id}/detail`}>
                  <IconButton size="small" aria-label="View Shipment" color="primary">
                    <InfoOutlined />
                  </IconButton>
                </Link>
                <Link
                  to={isEditDisabled ? "#" : `${basePath}/${row?._id}/edit`}
                  style={{ pointerEvents: isEditDisabled ? "none" : "auto" }}
                >
                  <IconButton
                    size="small"
                    aria-label="Edit Shipment"
                    disabled={isEditDisabled}
                    sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
                  >
                    <ModeEditOutlineTwoTone />
                  </IconButton>
                </Link>
              </>
            )}
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipment, { data: shipmentRes, isLoading, isFetching, error }] =
    useLazyListShipmentQuery();

  const handleApproveClick = (shipment) => {
    setSelectedShipment(shipment);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedShipment(null);
  };

  const handlePrintLabelModalOpen = () => setPrintLabelModalOpen(true);
  const handlePrintLabelModalClose = () => {
    setPrintLabelModalOpen(false);
    reset({ selectedShipments: [] });
  };

  const handleGenerateLabels = (validShipments) => {
    setSelectedShipments(validShipments);
    setPrintLabelModalOpen(false);
    setMultiLabelOpen(true);
  };

  const handleMultiLabelClose = () => {
    setMultiLabelOpen(false);
    setSelectedShipments([]);
    reset({ selectedShipments: [] });
  };

  const handleReopenPrintModal = () => {
    setMultiLabelOpen(false);
    setPrintLabelModalOpen(true);
  };

  const handleOpenBulkRoutes = () => setBulkRoutesOpen(true);
  const handleCloseBulkRoutes = () => setBulkRoutesOpen(false);
  const handleOpenBulkShipmentNote = () => setBulkShipmentNoteOpen(true);
  const handleCloseBulkShipmentNote = () => setBulkShipmentNoteOpen(false);

  const fetchRegions = () => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      // Fetch all regions for destination
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch regions:", error));

      // Fetch restricted regions for source
      triggerGetRestrictedRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch restricted regions:", error));
    }
  };

  const handleSourceRegionChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";
    appendParams({ sourceRegion: regionId, sourceBranch: "" });
    if (regionId && viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetSourceBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch source region branches:", error));
    }
  };

  const handleDestinationRegionChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";
    appendParams({ destinationRegion: regionId, destinationBranch: "" });
    if (regionId && viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDestinationBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch destination region branches:", error));
    }
  };

  useEffect(() => {
    const [sortedBy, sortDirection] = sortOrder.split(":");
    const cleanedKeyword = keyword ? keyword.replace(/-/g, "") : "";

    const finalSourceBranchIds = sourceRegion ? sourceBranchIdsFromRegion : sourceBranch;
    const finalDestinationBranchIds = destinationRegion ? destinationBranchIdsFromRegion : destinationBranch;

    fetchShipment({
      active: activeTab === "Active" ? true : activeTab === "Inactive" ? false : undefined,
      page: currentPage,
      limit: perPage,
      from,
      to,
      ...(cleanedKeyword && { keyword: cleanedKeyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
      ...(priority && { priorityId: priority }),
      ...(deliveryTypes && { types: deliveryTypes }),
      ...(!!finalDestinationBranchIds?.length && { destinationBranchId: finalDestinationBranchIds }),
      ...(!!finalSourceBranchIds?.length && { sourceBranchId: finalSourceBranchIds }),
      ...(paymentStatus && { paymentStatus }),
      ...(shipmentStatus === "IsReturning"
        ? { IsReturning: true }
        : shipmentStatus === "OnHold"
        ? { isOnHold: true }
        : shipmentStatus && { status: shipmentStatus }),
      ...(isVendor && { vendorId }),
      ...(activeTab === "OnHold"
        ? { isOnHold: true }
        : activeTab !== "Active" && activeTab !== "Inactive" && { status: activeTab }),
    });
  }, [
    activeTab, currentPage, deliveryTypes, fetchShipment, keyword, perPage,
    priority, destinationBranch, sourceBranch, sourceRegion, destinationRegion,
    sourceBranchIdsFromRegion, destinationBranchIdsFromRegion, paymentStatus,
    shipmentStatus, sortOrder, isVendor, vendorId, from, to,
  ]);

  useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDDLBranchList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetDDLDestinationBranchList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetRestrictedRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      if (sourceRegion) {
        triggerGetSourceBranchesByRegion({
          regionId: sourceRegion,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        });
      }
      if (destinationRegion) {
        triggerGetDestinationBranchesByRegion({
          regionId: destinationRegion,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        });
      }
    }
  }, [
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    sourceRegion,
    destinationRegion,
    triggerGetDDLBranchList,
    triggerGetDDLDestinationBranchList,
    triggerGetDdlRegionList,
    triggerGetRestrictedRegionList,
    triggerGetSourceBranchesByRegion,
    triggerGetDestinationBranchesByRegion,
  ]);

  useAppMeta(`${activeTab} Shipment`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={
        <PageActions
          onPrintLabelClick={handlePrintLabelModalOpen}
          onOpenBulkRoutes={handleOpenBulkRoutes}
          onOpenBulkShipmentNote={handleOpenBulkShipmentNote}
          viewEntity={viewEntity}
          loggedInUser={loggedInUser}
        />
      }
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={shipmentTabs}
          defaultValue={activeTab}
          actions={
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              <UploadExcelShipment />
              <ExportShipmentExcel />
            </Box>
          }
        />

        <TableFilter
          searchLabel="Shipment ID"
          disabled={isLoading || isFetching}
          initialFilters={3}
          onClear={() => resetParams()}
        >
          {/* Source Branch — restricted */}
          <BaseAutocomplete
            fullWidth
            multiple
            label="Source Branch"
            value={selectedSource}
            options={mappedBranchList}
            limitTags={2}
            onChange={(_, data) => {
              appendParams({
                sourceBranch: data.map((branch) => branch.value).join(","),
                sourceRegion: "",
              });
            }}
            onFocus={() => {
              if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
                triggerGetDDLBranchList({
                  account: viewEntity.entity.account._id,
                  entity: viewEntity.entity._id,
                }).unwrap().catch((error) => console.error("Failed to fetch source branches:", error));
              }
            }}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          />

          {/* Destination Branch — all branches */}
          <BaseAutocomplete
            fullWidth
            multiple
            label="Destination Branch"
            value={selectedDestination}
            limitTags={2}
            options={mappedDestinationBranchList}
            onChange={(_, data) => {
              appendParams({
                destinationBranch: data.map((branch) => branch.value).join(","),
                destinationRegion: "",
              });
            }}
            onFocus={() => {
              if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
                triggerGetDDLDestinationBranchList({
                  account: viewEntity.entity.account._id,
                  entity: viewEntity.entity._id,
                }).unwrap().catch((error) => console.error("Failed to fetch destination branches:", error));
              }
            }}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          />

          <BaseSelect
            fullWidth
            label="Shipment Status"
            value={shipmentStatus}
            onChange={(e) => appendParams({ shipmentStatus: e.target.value })}
            options={[
              { label: "All Statuses", value: "" },
              { label: "Pending", value: "Pending" },
              { label: "Collected", value: "Collected" },
              { label: "Consolidation", value: "Consolidation" },
              { label: "Loaded", value: "Loaded" },
              { label: "Unloaded", value: "Unloaded" },
              { label: "Deconsolidation", value: "Deconsolidation" },
              { label: "CrossDocked", value: "CrossDocked" },
              { label: "SendOut", value: "SendOut" },
              { label: "Warehousing", value: "Warehousing" },
              { label: "Return", value: "Return" },
              { label: "Not Delivered", value: "NotDelivered" },
              { label: "Delivered", value: "Delivered" },
              { label: "DeliveryFailed", value: "DeliveryFailed" },
              { label: "OnHold", value: "OnHold" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          {/* Source Province — restricted */}
          <BaseAutocomplete
            fullWidth
            label="Source Province"
            value={selectedSourceRegion || null}
            options={mappedRestrictedRegionList}
            onChange={handleSourceRegionChange}
            onFocus={fetchRegions}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          />

          {/* Destination Province — all regions */}
          <BaseAutocomplete
            fullWidth
            label="Destination Province"
            value={selectedDestinationRegion || null}
            options={mappedRegionList}
            onChange={handleDestinationRegionChange}
            onFocus={fetchRegions}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          />

          <BaseSelect
            fullWidth
            label="Delivery Types"
            value={deliveryTypes}
            onChange={(e) => appendParams({ deliveryTypes: e.target.value })}
            options={[
              { label: "All Types", value: "" },
              { label: "Express", value: "Express" },
              { label: "Regular", value: "Regular" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <BaseSelect
            fullWidth
            label="Payment Status"
            value={paymentStatus}
            onChange={(e) => appendParams({ paymentStatus: e.target.value })}
            options={[
              { label: "All Statuses", value: "" },
              { label: "Unpaid", value: "Unpaid" },
              { label: "Partially Paid", value: "Partially Paid" },
              { label: "Fully Paid", value: "Fully Paid" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => appendParams({ sortOrder: e.target.value })}
            options={[
              { label: "Created At (Desc)", value: "createdAt:-1" },
              { label: "Created At (Asc)", value: "createdAt:1" },
              { label: "Updated At (Desc)", value: "updatedAt:-1" },
              { label: "Updated At (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <TextField
            fullWidth
            label="From Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={from || ""}
            onChange={(e) => appendParams({ from: e.target.value })}
          />

          <TextField
            fullWidth
            label="To Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={to || ""}
            onChange={(e) => appendParams({ to: e.target.value })}
          />
        </TableFilter>

        {(sourceRegion && sourceBranchIdsFromRegion.length === 0) ||
          (destinationRegion && destinationBranchIdsFromRegion.length === 0) ? (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography variant="h6">No shipments found in this region</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The selected region doesn't have any branches with shipments.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            {isLoading || isFetching ? (
              <TableLoader />
            ) : !(shipmentRes?.data ?? []).length ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1">
                  {sourceRegion || destinationRegion
                    ? "No shipments found in this region"
                    : "No shipments found"}
                </Typography>
              </Box>
            ) : (
              <>
                {(shipmentRes?.data ?? []).map((row) => (
                  <ShipmentMobileCard
                    key={row?._id}
                    row={row}
                    keyword={keyword}
                    onApprove={handleApproveClick}
                  />
                ))}
                <TablePagination
                  component="div"
                  count={shipmentRes?.totalItems ?? 0}
                  page={(currentPage || 1) - 1}
                  onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
                  rowsPerPage={perPage}
                  onRowsPerPageChange={(e) =>
                    handlePerRowsChange(parseInt(e.target.value, 10))
                  }
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </>
            )}
            <MobileScrollArrows />
          </Box>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
          <AppTable
            columns={columns}
            data={shipmentRes?.data ?? []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={shipmentRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6">
                  {sourceRegion || destinationRegion
                    ? "No shipments found in this region"
                    : "No shipments found"}
                </Typography>
              </Box>
            }
          />
          </Box>
        )}

        {selectedShipment && (
          <CollectionModal
            open={modalOpen}
            onClose={handleModalClose}
            shipment={selectedShipment}
          />
        )}
        <PrintShipmentLabelModal
          open={printLabelModalOpen}
          onClose={handlePrintLabelModalClose}
          onGenerateLabels={handleGenerateLabels}
          accountId={viewEntity?.entity?.account?._id}
          entityId={viewEntity?.entity?._id}
        />
        <MultiShipmentLabel
          isOpen={multiLabelOpen}
          onClose={handleMultiLabelClose}
          onReopenPrintModal={handleReopenPrintModal}
          shipments={selectedShipments}
        />
        <BulkRoutesModalForm
          open={bulkRoutesOpen}
          onClose={handleCloseBulkRoutes}
          accountId={viewEntity?.entity?.account?._id}
          entityId={viewEntity?.entity?._id}
        />
        <BulkShipmentNoteModalForm
          open={bulkShipmentNoteOpen}
          onClose={handleCloseBulkShipmentNote}
          accountId={viewEntity?.entity?.account?._id}
          entityId={viewEntity?.entity?._id}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const MobileScrollArrows = () => {
  const [showTopArrow, setShowTopArrow] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setShowTopArrow(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        right: 16,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        zIndex: (theme) => theme.zIndex.fab,
      }}
    >
      <Zoom in={showTopArrow}>
        <Fab
          size="small"
          color="primary"
          aria-label="Scroll to top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <KeyboardArrowUp />
        </Fab>
      </Zoom>
      <Fab
        size="small"
        color="primary"
        aria-label="Scroll to bottom"
        onClick={() =>
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
          })
        }
      >
        <KeyboardArrowDown />
      </Fab>
    </Box>
  );
};

const ShipmentMobileCard = ({ row, keyword, onApprove }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isVendor, isCustomer, isVendorStaff, isContractor } = useAuth();

  const shiId = row?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1);
  const displayId = `${shiId}${row.IsReturning ? " (R)" : ""}`;
  const basePath = isVendor
    ? "/app/vendor/shipment"
    : isCustomer
      ? "/app/customer/shipment"
      : isVendorStaff
        ? "/app/staff/shipment"
        : "/app/crm/shipment";
  const detailPath = `${basePath}/${row?._id}/detail`;
  const isEditDisabled =
    ((isVendor || isCustomer || isVendorStaff) && row?.status !== "Pending") ||
    row?.status === "Delivered" ||
    row?.isActive !== true;
  const statusStyle = getStatusStyle(row?.status);
  const senderName = row?.customerId?.customerUser?.name;
  const vendorName = row?.vendor?.shopName;
  const receiverName = row?.receiver?.customerUser?.name || row?.receiver?.name || "";
  const receiverPhone = row?.receiver?.customerUser?.phone || row?.receiver?.phone || "";

  const renderDetailItem = (icon, label, value, iconColor = theme.palette.primary.main) => (
    <Box sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1,
          flexShrink: 0,
          backgroundColor: alpha(iconColor, 0.15),
          color: iconColor,
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ fontWeight: 500, textAlign: "right", maxWidth: "70%" }}
        >
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        mb: 1.25,
        p: 1,
        borderRadius: 0,
        backgroundColor: "action.hover",
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.selected" },
      }}
      role="button"
      tabIndex={0}
      onClick={() => navigate(detailPath)}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(detailPath);
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1,
              flexShrink: 0,
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              color: "primary.main",
            }}
          >
            <Inventory2Outlined sx={{ fontSize: 18 }} />
          </Box>
          <Link
            title={displayId}
            to={detailPath}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <Typography noWrap sx={{ fontSize: 15, fontWeight: 600 }}>
              {displayId ? <HighlightedText text={displayId} highlight={keyword} /> : "N/A"}
            </Typography>
          </Link>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          <Box
            sx={{
              px: 1,
              py: "2px",
              borderRadius: "12px",
              border: "1px solid",
              borderColor: statusStyle.textColor,
              backgroundColor: statusStyle.backgroundColor,
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 600, lineHeight: "14px", color: statusStyle.textColor }}>
              {row?.status?.toUpperCase() || "N/A"}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isContractor ? (
              <IconButton
                size="small"
                aria-label="Approve Shipment"
                color="primary"
                onClick={() => onApprove(row)}
                disabled={!row}
              >
                <CheckCircleOutline sx={{ fontSize: 20 }} />
              </IconButton>
            ) : (
              <>
                <IconButton
                  size="small"
                  aria-label="View Shipment"
                  color="primary"
                  onClick={() => navigate(detailPath)}
                >
                  <InfoOutlined sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Edit Shipment"
                  disabled={isEditDisabled}
                  onClick={() => navigate(`${basePath}/${row?._id}/edit`)}
                  sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
                >
                  <ModeEditOutlineTwoTone sx={{ fontSize: 20 }} />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {(senderName || vendorName) &&
          renderDetailItem(
            senderName ? (
              <PersonOutlineOutlined sx={{ fontSize: 16 }} />
            ) : (
              <StorefrontOutlined sx={{ fontSize: 16 }} />
            ),
            "Sender",
            senderName ? `CUS: ${senderName}` : `VEN: ${vendorName}`,
            theme.palette.info.main
          )}

        {(receiverName || receiverPhone) &&
          renderDetailItem(
            <ContactPageOutlined sx={{ fontSize: 16 }} />,
            "Receiver",
            [receiverName, receiverPhone].filter(Boolean).join(" \u2022 "),
            theme.palette.success.main
          )}

        {renderDetailItem(
          <LocalShippingOutlined sx={{ fontSize: 16 }} />,
          "Route",
          `${row?.sourceBranch?.name || "N/A"} \u2192 ${row?.destinationBranch?.name || "N/A"}`,
          theme.palette.primary.main
        )}

        {renderDetailItem(
          <AccountCircleOutlined sx={{ fontSize: 16 }} />,
          "Created By",
          row?.createdBy?.name,
          theme.palette.warning.main
        )}

        {renderDetailItem(
          <EventNoteOutlined sx={{ fontSize: 16 }} />,
          "Created At",
          <AppTableDateTime date={row?.createdAt} />,
          theme.palette.secondary.main
        )}
      </Box>
    </Box>
  );
};

const PageActions = ({
  onPrintLabelClick,
  onOpenBulkRoutes,
  onOpenBulkShipmentNote,
  viewEntity,
  loggedInUser,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showBulkPrintModal, setShowBulkPrintModal] = useState(false);
  const {
    isVendor, isCustomer, isVendorStaff, isDriver,
    isContractor, isAccountAdmin, isSystemAdmin,
  } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
return (
    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2 }}>
        {!isVendor && !isCustomer && !isVendorStaff && (
          <Link to="/app/crm/shipment/collect?addAndCollect=true" style={{ textDecoration: "none" }}>
            <Button
              startIcon={<PlaylistAddCheck />}
              variant="outlined"
              color="primary"
            >
              Add & Collect
            </Button>
          </Link>
        )}

        <Link
          to={
            isVendor
              ? "/app/vendor/shipment/create"
              : isCustomer
                ? "/app/customer/shipment/create"
                : isVendorStaff
                  ? "/app/staff/shipment/create"
                  : "/app/crm/shipment/create"
          }
          style={{ textDecoration: "none" }}
        >
          <Button
            startIcon={<Add />}
            variant="contained"
            color="primary"
          >
            Add Shipment
          </Button>
        </Link>
      </Box>

      {!isContractor && (
        <>
          <Box sx={{ ml: "auto" }}>
            <Tooltip title="Actions">
              <IconButton
                color="primary"
                aria-label="Actions"
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <MoreVert />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => { onPrintLabelClick(); setAnchorEl(null); }}>
              <Print sx={{ mr: 1 }} /> Print
            </MenuItem>
            <MenuItem onClick={() => { setShowBulkPrintModal(true); setAnchorEl(null); }}>
              <Print sx={{ mr: 1 }} /> Bulk Invoice Print
            </MenuItem>
            {(isAccountAdmin || isSystemAdmin) && (
              <MenuItem
                component={Link}
                to="/app/crm/shipment/bulkCancelScreen"
                onClick={() => setAnchorEl(null)}
              >
                <HighlightOff sx={{ mr: 1 }} /> Cancel Shipments
              </MenuItem>
            )}
            {!isCustomer && !isVendor && !isVendorStaff && !isDriver && (
              <MenuItem onClick={() => { onOpenBulkRoutes(); setAnchorEl(null); }}>
                <RouteSharp sx={{ mr: 1 }} /> Add Routes
              </MenuItem>
            )}
            {!isCustomer && !isDriver && (
              <MenuItem onClick={() => { onOpenBulkShipmentNote(); setAnchorEl(null); }}>
                <Note sx={{ mr: 1 }} /> Add Bulk Note
              </MenuItem>
            )}
          </Menu>
          {showBulkPrintModal && (
            <BulkInvoicePrintModal
              isOpen={showBulkPrintModal}
              onClose={() => setShowBulkPrintModal(false)}
              entity={viewEntity?.entity}
              loggedInUser={loggedInUser}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ShipmentListScreen;